import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import PriceLabel from '@prisjakt/ui-price-label';
import StoreLogo, { VARIANTS as LOGO_VARIANTS } from '@prisjakt/ui-store-logo';

import * as S from './styles';

const ProductCard = ({
  onClick,
  category,
  linkAttributes,
  link,
  campaignBadge,
  image,
  brandLogo,
  rating,
  ratingCount,
  ratingCountTranslation,
  name,
  subtitle,
  price,
  diffPercentage,
  isCampaignItem,
  hasDivider,
  store,
  className,
  children,
  footer,
  selectable,
  selected,
  onSelect,
  onDeselect,
  externalLink,
}) => {
  const onToggle = selected ? onDeselect : onSelect;
  const clickHandler = useCallback(
    e => {
      if (selectable) {
        e.preventDefault();
        onToggle();
        return;
      }

      if (externalLink) {
        e.preventDefault();
        window.location.href = e.currentTarget.href;
      }

      onClick(e);
    },
    [selectable, onToggle, externalLink, onClick]
  );

  return (
    <S.Card className={className} hasDivider={hasDivider}>
      <S.ProductLink
        onClick={clickHandler}
        {...linkAttributes}
        to={link}
        aria-label={name}
        selected={selected}
      >
        {diffPercentage && (
          <S.DiffPercentage>-{Math.round(diffPercentage)}%</S.DiffPercentage>
        )}
        {campaignBadge && <S.CampaignBadge>{campaignBadge}</S.CampaignBadge>}
        {image && (
          <S.ProductImage>
            {image}
            {brandLogo && <S.BrandLogo>{brandLogo}</S.BrandLogo>}
          </S.ProductImage>
        )}

        <S.ProductDetails stretch={isCampaignItem}>
          <S.ProductName>{name}</S.ProductName>
          {subtitle && <S.Subtitle>{subtitle}</S.Subtitle>}
          <S.Rating
            hide={rating === null || rating === undefined}
            rate={rating}
            centerAlign
            count={ratingCount}
            countTranslation={ratingCountTranslation}
            showCounter
            size="small"
          />
        </S.ProductDetails>

        <S.PriceInfo>
          {price && (
            <div>
              <PriceLabel value={price} />
            </div>
          )}
          {store && (
            <StoreLogo
              variant={LOGO_VARIANTS.CAMPAIGN}
              name={store.name}
              storeId={store.id}
              featured
              hasImage={store.hasLogo}
            />
          )}
        </S.PriceInfo>

        {children && <S.ExtraFooter>{children}</S.ExtraFooter>}
      </S.ProductLink>
      {category && category.pathName && (
        <S.CategoryArea>
          <S.CategoryLink
            size="small"
            color="secondary"
            variant="outlined"
            href={category.pathName}
            truncate
          >
            {category.name}
          </S.CategoryLink>
        </S.CategoryArea>
      )}
      {footer && <S.Footer>{footer}</S.Footer>}
      {selectable && (
        <S.Checkbox
          selected={selected}
          onSelect={onSelect}
          onDeselect={onDeselect}
          aria-label={name}
        />
      )}
    </S.Card>
  );
};

const {
  func,
  string,
  number,
  oneOfType,
  element,
  node,
  shape,
  bool,
} = PropTypes;

ProductCard.propTypes = {
  onClick: func,
  link: string.isRequired,
  image: node,
  name: string.isRequired,
  rating: number,
  ratingCount: number,
  ratingCountTranslation: shape({
    domain: string,
    key: string,
  }),
  price: oneOfType([string, number, element]),
  diffPercentage: number,
  isCampaignItem: bool,
  children: node,
  campaignBadge: element,
  subtitle: node,
  brandLogo: node,
  linkAttributes: shape({
    target: string,
    rel: string,
  }),
  category: shape({
    name: string.isRequired,
    pathName: string,
  }),
  store: shape({
    name: string.isRequired,
    id: number.isRequired,
    hasLogo: bool.isRequired,
  }),
  hasDivider: bool,
  className: string,
  footer: node,
  selectable: bool,
  selected: bool,
  onSelect: func,
  onDeselect: func,
  externalLink: bool,
};

const noop = () => {};

ProductCard.defaultProps = {
  onClick: noop,
  image: undefined,
  rating: undefined,
  ratingCount: undefined,
  ratingCountTranslation: {
    domain: 'product',
    key: 'product-card.n-ratings',
  },
  price: undefined,
  diffPercentage: undefined,
  isCampaignItem: false,
  children: undefined,
  campaignBadge: undefined,
  subtitle: undefined,
  brandLogo: undefined,
  linkAttributes: undefined,
  category: undefined,
  store: undefined,
  hasDivider: false,
  className: undefined,
  footer: undefined,
  selectable: false,
  selected: false,
  onSelect: noop,
  onDeselect: noop,
  externalLink: false,
};

export default ProductCard;
